type RGB = {
  r: number;
  g: number;
  b: number;
};

type RGBA = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export enum RGB_FORMAT_OUTPUT {
  COMMA_SEPARATED_STRING,
  ARRAY,
  HASH_STRING = 2,
}

type HEX = string;

type HEX_ALPHA = string;

const hexToDecimal = (hex: string) => parseInt(hex, 16);

const decimalToHex = (decimal: number) =>
  Math.round(decimal).toString(16).padStart(2, "0");

const isValidInt = (value: string) => {
  try {
    const parsedValue = parseInt(value);
    if (isNaN(parsedValue)) return false;
  } catch {
    return false;
  }
  return true;
};

export const hue2rgb = (p: number, q: number, t: number) => {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
};

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const isHexColor = (value: unknown): value is string => {
  return typeof value === "string" && HEX_COLOR_REGEX.test(value);
};

export const convertHexToRGB = (color: null | string | undefined) => {
  const rgb = hexToRgb(color ?? "", RGB_FORMAT_OUTPUT.ARRAY);
  return Array.isArray(rgb) ? (rgb as number[]) : [0, 0, 0];
};

export function componentToHex(c: number) {
  const hex = Math.round(c).toString(16);
  return hex.length == 1 ? "0" + hex : hex;
}

export function rgbaToHex(r: number, g: number, b: number, a: number) {
  return (
    "#" +
    componentToHex(r) +
    componentToHex(g) +
    componentToHex(b) +
    componentToHex(a)
  );
}

export function rgbToHex(r: number, g: number, b: number) {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

export interface HslColor {
  hue: string;
  lightness: string;
  saturation: string;
}

export const rgbToHsl = (r: number, g: number, b: number) => {
  r = r / 255;
  g = g / 255;
  b = b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case r:
        h = 60 * (((g - b) / delta) % 6);
        break;
      case g:
        h = 60 * ((b - r) / delta + 2);
        break;
      case b:
        h = 60 * ((r - g) / delta + 4);
        break;
    }
  }

  if (h < 0) h += 360;

  h = Math.round(h);
  s = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  const hslColor: HslColor = {
    hue: h.toString(),
    lightness: lPercent.toString(),
    saturation: s.toString(),
  };

  return hslColor;
};

// receives a 'rgb(R,G,B)' and returns 'R,G,B'
export function extractRGBValues(input: string): null | string {
  const rgbValues: number[] | null =
    input
      .match(/\d+/g) // Retrieve all numbers
      ?.map(Number) || null;

  if (!rgbValues || rgbValues.length < 3) {
    return null;
  }

  const [r, g, b] = rgbValues;

  return `${r}, ${g}, ${b}`;
}

export function hexToRgb(hex: string, mode?: RGB_FORMAT_OUTPUT) {
  let r,
    g,
    b,
    a = "";
  if (hex === "") hex = "000000";
  if (hex.charAt(0) === "#") hex = hex.substring(1, hex.length);
  if (hex.length !== 6 && hex.length !== 8 && hex.length !== 3) {
    console.error("Please enter 6 digits color code !", hex);
    return;
  }
  if (hex.length === 3) {
    r = hex.substring(0, 1);
    g = hex.substring(1, 2);
    b = hex.substring(2, 3);
    r = r + r;
    g = g + g;
    b = b + b;
  } else {
    r = hex.substring(0, 2);
    g = hex.substring(2, 4);
    b = hex.substring(4, 6);
  }
  if (hex.length === 8) {
    a = hex.substring(6, 8);
    a = (parseInt(a, 16) / 255.0).toFixed(2);
  }
  r = parseInt(r, 16);
  g = parseInt(g, 16);
  b = parseInt(b, 16);

  if (mode === RGB_FORMAT_OUTPUT.ARRAY) {
    return [r, g, b, a];
  }

  if (!mode || mode === RGB_FORMAT_OUTPUT.HASH_STRING) {
    return `${r}, ${g}, ${b}`;
  }

  return null;
}
export function hexToRGBA(
  hex: string,
  alpha?: number,
): null | [number, number, number, number | null] {
  const result =
    /^#?(?<r>[a-f\d]{2})(?<g>[a-f\d]{2})(?<b>[a-f\d]{2})(?<a>[a-f\d]{2})?$/i.exec(
      hex,
    );

  return result?.groups
    ? [
        parseInt(result.groups.r, 16),
        parseInt(result.groups.g, 16),
        parseInt(result.groups.b, 16),
        alpha ?? (parseInt(result.groups.a, 16) || 255),
      ]
    : null;
}

export const shadeColor = (
  color: string,
  percent: number,
  attempt?: number,
): string => {
  if (attempt >= 10) return color;
  if (!color.includes("#")) color = "#" + color;
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = (R * (100 + percent)) / 100;
  G = (G * (100 + percent)) / 100;
  B = (B * (100 + percent)) / 100;

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  const RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

  const brightness = Math.round((R * 299 + G * 587 + B * 114) / 1000);

  if (brightness == 0) return complementryHexColor(color);
  if (brightness < 80)
    return shadeColor(color, percent + 50, (attempt || 0) + 1);

  return "#" + RR + GG + BB;
};

export const checkHexFormat = (color: string): string => {
  if (!color.includes("#")) color = "#" + color;
  return color;
};

//complementary colors
function complementryHexColor(hex: string) {
  let r =
    hex.length == 4
      ? parseInt(hex[1] + hex[1], 16)
      : parseInt(hex.slice(1, 3), 16);
  let g =
    hex.length == 4
      ? parseInt(hex[2] + hex[2], 16)
      : parseInt(hex.slice(3, 5), 16);
  let b =
    hex.length == 4
      ? parseInt(hex[3] + hex[3], 16)
      : parseInt(hex.slice(5), 16);

  [r, g, b] = complementryRGBColor(r, g, b);

  const result =
    "#" +
    (r < 16 ? "0" + r.toString(16) : r.toString(16)) +
    (g < 16 ? "0" + g.toString(16) : g.toString(16)) +
    (b < 16 ? "0" + b.toString(16) : b.toString(16));

  return result;
}

export function invertColor(rgb: number[], bw: boolean) {
  let hex = rgbToHex(rgb[0], rgb[1], rgb[2]);
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  let r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);
  if (bw) {
    // https://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186
      ? [0, 0, 0]
      : [255, 255, 255];
  }
  // invert color components
  r = Number((255 - r).toString(16) as string);
  g = Number((255 - g).toString(16) as string);
  b = Number((255 - b).toString(16) as string);
  // pad each with zeros and return
  return [r, g, b];
}

// Applies sRGB gamma expansion (IEC 61966-2-1): linearizes a single 0-255 channel
const linearizeChannel = (c: number): number => {
  const srgb = c / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
};

// Computes WCAG 2.x relative luminance from raw sRGB values (0-255) — linearizes internally
export const relativeLuminance = (r: number, g: number, b: number): number => {
  return (
    0.2126 * linearizeChannel(r) +
    0.7152 * linearizeChannel(g) +
    0.0722 * linearizeChannel(b)
  );
};

// Computes WCAG 2.x contrast ratio between two relative luminances
export const contrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export function complementryRGBColor(r: number, g: number, b: number) {
  if (Math.max(r, g, b) === Math.min(r, g, b)) {
    return [255 - r, 255 - g, 255 - b];
  } else {
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0;
    const l = (max + min) / 2;
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h = Math.round(h * 60 + 180) % 360;
    h /= 360;

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }
}

export const darkenColor = (color: number[], darkness: number) => {
  // Only darken r, g, b components
  return color.map((value, idx) =>
    idx < 3 ? Math.round(value * (1 - darkness)) : value,
  );
};

export const complementaryRGBColor = (r: number, g: number, b: number) => {
  if (Math.max(r, g, b) === Math.min(r, g, b)) {
    return { b: 255 - b, g: 255 - g, r: 255 - r };
  } else {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0;
    const l = (max + min) / 2;
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h = Math.round(h * 60 + 180) % 360;
    h /= 360;

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);

    return {
      b: Math.round(b * 255),
      g: Math.round(g * 255),
      r: Math.round(r * 255),
    };
  }
};

/*****************************************
 ************** Color class **************
 *****************************************/
export class Color {
  r = 0;
  g = 0;
  b = 0;
  a = 255;

  constructor(data: string | number[] | Color) {
    if (data instanceof Color) {
      this.r = data.r;
      this.g = data.g;
      this.b = data.b;
      this.a = data.a;
    } else if (typeof data === "string") {
      if (data.startsWith("#")) {
        this._parseHexString(data);
      } else if (data.startsWith("rgb(")) {
        this._parseRGBString(data);
      } else if (data.startsWith("rgba(")) {
        this._parseRGBAString(data);
      } else if (isValidInt(data)) {
        this._parseHexString(`#${data}`);
      } else {
        try {
          this._readColorFromDOM(data);
        } catch {
          throw Error(`Format not supported for color string: "${data}"`);
        }
      }
    } else if (Array.isArray(data)) {
      this._parseColorArray(data);
    } else {
      throw Error(`Format not supported for color: "${typeof data}"`);
    }
  }

  ////////////////////////////
  // INTERNAL PARSING FUNCTIONS
  ////////////////////////////

  _readColorFromDOM(color: string) {
    const d = document.createElement("div");
    d.style.color = color;
    document.body.appendChild(d);
    const parsedColor = window.getComputedStyle(d).color;
    this._parseRGBString(parsedColor);
  }

  _parseColorArray(data: any[]) {
    const colorArray = data.map((x) => parseInt(x));
    if (colorArray.length < 3) {
      throw Error(
        `Invalid array format color string: "${data}"\nSupported formats: [r,g,b] | [r,g,b,a]`,
      );
    }
    this.r = colorArray[0];
    this.g = colorArray[1];
    this.b = colorArray[2];
    this.a = colorArray.at(3) ?? this.a;
  }

  _parseRGBString(data: string) {
    const colorString = data.replace("rgb(", "").replace(")", "");
    const colorComponents = colorString.split(",");
    if (data.indexOf("rgb(") == -1 || colorComponents.length != 3) {
      throw Error(`Invalid 'rgb(r,g,b)' format for color string: "${data}"`);
    }
    this.r = parseInt(colorComponents[0]);
    this.g = parseInt(colorComponents[1]);
    this.b = parseInt(colorComponents[2]);
  }

  _parseRGBAString(data: string) {
    const colorString = data.replace("rgba(", "").replace(")", "");
    const colorComponents = colorString.split(",");
    if (data.indexOf("rgba(") == -1 || colorComponents.length != 4) {
      throw Error(`Invalid 'rgba(r,g,b,a)' format for color string: "${data}"`);
    }
    this.r = parseInt(colorComponents[0]);
    this.g = parseInt(colorComponents[1]);
    this.b = parseInt(colorComponents[2]);
    this.a = parseInt(colorComponents[3]);
  }

  _parseHexString(data: string) {
    const colorString = data.replace("#", "");

    switch (colorString.length) {
      case 3:
        this.r = hexToDecimal(
          colorString.slice(0, 1) + colorString.slice(0, 1),
        );
        this.g = hexToDecimal(
          colorString.slice(1, 2) + colorString.slice(1, 2),
        );
        this.b = hexToDecimal(
          colorString.slice(2, 3) + colorString.slice(2, 3),
        );
        break;
      case 6:
        this.r = hexToDecimal(colorString.slice(0, 2));
        this.g = hexToDecimal(colorString.slice(2, 4));
        this.b = hexToDecimal(colorString.slice(4, 6));
        break;
      case 8:
        this.r = hexToDecimal(colorString.slice(0, 2));
        this.g = hexToDecimal(colorString.slice(2, 4));
        this.b = hexToDecimal(colorString.slice(4, 6));
        this.a = hexToDecimal(colorString.slice(6, 8));
        break;
      default:
        throw Error(`Invalid hex format for color string: "${data}"`);
    }
  }

  ////////////////////////////
  // COLOR TRANSFORMATIONS
  ////////////////////////////

  /**
   * Changes the opacity value of this color
   *
   * @param opacity Opacity value [0,1]
   * @returns A new `Color` instance with the adjusted opacity.
   */
  opacity(opacity: number): Color {
    this.a = Math.max(0, Math.min(opacity * 255, 255));
    return this;
  }

  /**
   * Creates a new complimentary color based on this instance
   *
   * @returns A new `Color` instance with complementary value.
   */
  complementary(): Color {
    const { r, g, b } = complementaryRGBColor(this.r, this.g, this.b);
    return new Color([r, g, b, this.a]);
  }

  /**
   * Adjusts the color brightness by a percentage to create a shaded variant.
   *
   * @param percent Percentage to adjust the shade. Positive values lighten the color, negative values darken it.
   * @returns A new `Color` instance with the adjusted shade.
   */
  shade(percent: number): Color {
    let R = (this.r * (100 + percent)) / 100;
    let G = (this.g * (100 + percent)) / 100;
    let B = (this.b * (100 + percent)) / 100;

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    R = Math.round(R);
    G = Math.round(G);
    B = Math.round(B);

    const brightness = Math.round((R * 299 + G * 587 + B * 114) / 1000);

    if (brightness == 0) return this.complementary();
    if (brightness < 80 && percent < 100) return this.shade(percent + 50);

    return new Color([R, G, B]);
  }

  /**
   * Computes a contrasting color (either black or white) based on the perceived brightness of this color.
   *
   * @returns A new `Color` instance representing the contrasting color.
   */
  contrastingColor(): Color {
    return new Color(this.luma() >= 165 ? "#000" : "#fff");
  }

  ////////////////////////////
  // DATA ACCESSORS
  ////////////////////////////
  /**
   * Calculates the luma (perceived brightness) of the color.
   * Uses the Rec. 709 formula to account for human eye sensitivity to different colors.
   *
   * @returns The luma value as a number ranging from 0 to 255.
   */
  luma(): number {
    return 0.2126 * this.r + 0.7152 * this.g + 0.0722 * this.b; // SMPTE C, Rec. 709 weightings
  }

  ////////////////////////////
  // OUTPUT FORMATS
  ////////////////////////////

  /**
   * Converts the color to an RGB format.
   *
   * @returns An object containing the red, green, and blue components of the color.
   */
  rgb(): RGB {
    return { b: this.b, g: this.g, r: this.r };
  }

  /**
   * Converts the color to an RGBA format.
   *
   * @returns An object containing the red, green, blue, and alpha (opacity) components of the color.
   */
  rgba(): RGBA {
    return { a: this.a, b: this.b, g: this.g, r: this.r };
  }

  /**
   * Converts the color to a hexadecimal (hex) string format.
   *
   * @returns A hex string representing the color in RGB format (e.g., `#RRGGBB`).
   */
  hex(): HEX {
    return `#${decimalToHex(this.r)}${decimalToHex(this.g)}${decimalToHex(this.b)}`;
  }

  /**
   * Converts the color to a hexadecimal (hex) string format including alpha (opacity) value.
   *
   * @param options - An object containing the format option ('rgba' for "#AARRGGBB" or 'argb' for "#RRGGBBAA").
   * @returns A hex string representing the color in RGBA format including alpha (opacity).
   */
  hexa(options?: { format: "argb" | "rgba" }): HEX_ALPHA {
    if (options?.format === "argb") {
      return `#${decimalToHex(this.a)}${decimalToHex(this.r)}${decimalToHex(this.g)}${decimalToHex(this.b)}`;
    }
    return `#${decimalToHex(this.r)}${decimalToHex(this.g)}${decimalToHex(this.b)}${decimalToHex(this.a)}`;
  }

  /**
   * Converts the color to an array format.
   *
   * @returns An array containing the red, green, blue, and alpha (opacity) components of the color.
   */
  array(): [number, number, number, number] {
    return [this.r, this.g, this.b, this.a];
  }
}
